import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Wallet, Cpu, CreditCard, Plus, Trash2, Save, Coins, Users } from "lucide-react";
import { AdminShell } from "./AdminShell";
import { adminApi } from "./adminApi";

const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const KPI = ({ icon: Icon, label, value, tone = "neutral", sub }) => {
  const color = tone === "good" ? "text-emerald-400" : tone === "bad" ? "text-red-400" : "text-white";
  return (
    <div className="bg-[#111114] border border-white/10 p-6" data-testid={`roi-kpi-${label}`}>
      <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider"><Icon className="w-4 h-4 text-[#C19A6B]" /> {label}</div>
      <div className={`text-3xl font-light mt-2 ${color}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
};

const Panel = ({ title, icon: Icon, children, action }) => (
  <div className="bg-[#111114] border border-white/10 p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2 text-sm text-white"><Icon className="w-4 h-4 text-[#C19A6B]" /> {title}</div>
      {action}
    </div>
    {children}
  </div>
);

export default function AdminROI({ embedded } = {}) {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [manual, setManual] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ kind: "revenue", label: "", amount: "", category: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [d, c, m] = await Promise.all([adminApi.roi(), adminApi.roiConfig(), adminApi.roiManualList()]);
      setData(d); setConfig(c); setManual(m);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveConfig = async () => {
    setSaving(true);
    try { const c = await adminApi.saveRoiConfig(config); setConfig(c); await load(); } catch (e) {}
    setSaving(false);
  };
  const addManual = async () => {
    if (!form.amount || Number(form.amount) <= 0) return;
    await adminApi.roiManualAdd({ ...form, amount: Number(form.amount) });
    setForm({ kind: "revenue", label: "", amount: "", category: "" });
    await load();
  };
  const delManual = async (id) => { await adminApi.roiManualDelete(id); await load(); };

  if (loading || !data) return <AdminShell embedded={embedded} title="Rentabilité (ROI)"><div className="text-zinc-500">Chargement…</div></AdminShell>;

  const maxMonth = Math.max(1, ...data.monthly.map((m) => Math.max(m.revenue, m.expense)));

  return (
    <AdminShell embedded={embedded} title="Rentabilité (ROI)">
      {/* KPI principaux */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Wallet} label="Revenus totaux" value={eur(data.total_revenue)} tone="good" sub={`Stripe ${eur(data.revenue.stripe)}${data.revenue.manual ? ` · Manuel ${eur(data.revenue.manual)}` : ""}`} />
        <KPI icon={Cpu} label="Dépenses totales" value={eur(data.total_expense)} tone="bad" sub={`IA ${eur(data.expense.ai_total)} · Frais Stripe ${eur(data.expense.stripe_fees)}`} />
        <KPI icon={data.profitable ? TrendingUp : TrendingDown} label="Résultat net" value={`${data.net >= 0 ? "🟢" : "🔴"} ${eur(data.net)}`} tone={data.profitable ? "good" : "bad"} sub={`Marge ${data.margin_pct}%`} />
        <KPI icon={Cpu} label="Appels IA journalisés" value={data.expense.ai_calls} sub="Claude · Whisper · Gemini" />
      </div>

      {/* Graphe mensuel + répartitions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2">
          <Panel title="Évolution mensuelle (revenus vs dépenses)" icon={TrendingUp}>
            {data.monthly.length === 0 ? <p className="text-xs text-zinc-500">Aucune donnée.</p> : (
              <div className="space-y-4" data-testid="roi-monthly">
                {data.monthly.map((m) => (
                  <div key={m.month}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-zinc-300">{m.month}</span>
                      <span className={m.net >= 0 ? "text-emerald-400" : "text-red-400"}>{m.net >= 0 ? "🟢" : "🔴"} {eur(m.net)}</span>
                    </div>
                    <div className="flex gap-1 items-center">
                      <div className="h-2 bg-emerald-500/70 rounded-sm" style={{ width: `${(m.revenue / maxMonth) * 100}%`, minWidth: m.revenue ? 6 : 0 }} title={`Revenus ${eur(m.revenue)}`} />
                    </div>
                    <div className="flex gap-1 items-center mt-1">
                      <div className="h-2 bg-red-500/70 rounded-sm" style={{ width: `${(m.expense / maxMonth) * 100}%`, minWidth: m.expense ? 6 : 0 }} title={`Dépenses ${eur(m.expense)}`} />
                    </div>
                  </div>
                ))}
                <div className="flex gap-4 text-[11px] text-zinc-500 pt-2">
                  <span className="flex items-center gap-1"><span className="w-3 h-2 bg-emerald-500/70 inline-block rounded-sm" /> Revenus</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-2 bg-red-500/70 inline-block rounded-sm" /> Dépenses</span>
                </div>
              </div>
            )}
          </Panel>
        </div>
        <div className="space-y-4">
          <Panel title="Sources de revenus" icon={Coins}>
            <div className="space-y-2" data-testid="roi-revenue-breakdown">
              {data.revenue_breakdown.map((r) => (
                <div key={r.key} className="flex justify-between text-sm">
                  <span className="text-zinc-300">{r.label}</span>
                  <span className="text-emerald-400">{eur(r.amount)}</span>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="Postes de dépenses" icon={CreditCard}>
            <div className="space-y-2" data-testid="roi-cost-breakdown">
              {data.cost_breakdown.map((c) => (
                <div key={c.key} className="flex justify-between text-sm">
                  <span className="text-zinc-300">{c.label}{c.count != null ? <span className="text-zinc-600"> ({c.count})</span> : null}</span>
                  <span className="text-red-400">{eur(c.cost)}</span>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>

      {/* Rentabilité par conseiller */}
      <div className="mt-6">
        <Panel title="Rentabilité par conseiller" icon={Users}>
          {data.advisors.length === 0 ? <p className="text-xs text-zinc-500">Aucune donnée.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="roi-advisors">
                <thead><tr className="text-xs uppercase tracking-wider text-zinc-500 text-left">
                  <th className="py-2">Conseiller</th><th className="py-2 text-right">Revenus</th><th className="py-2 text-right">Coûts IA</th><th className="py-2 text-right">Net</th>
                </tr></thead>
                <tbody className="divide-y divide-white/5">
                  {data.advisors.map((a) => (
                    <tr key={a.member_id} data-testid={`roi-advisor-${a.member_id}`}>
                      <td className="py-2.5 text-zinc-200">{a.name}{a.company ? <span className="text-zinc-600"> · {a.company}</span> : null}</td>
                      <td className="py-2.5 text-right text-emerald-400">{eur(a.revenue)}</td>
                      <td className="py-2.5 text-right text-red-400">{eur(a.ai_cost)}</td>
                      <td className={`py-2.5 text-right font-medium ${a.net >= 0 ? "text-emerald-400" : "text-red-400"}`}>{a.net >= 0 ? "🟢" : "🔴"} {eur(a.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {/* Barème IA + frais Stripe */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <Panel title="Barème des coûts IA (€/appel)" icon={Cpu} action={
          <button onClick={saveConfig} disabled={saving} data-testid="roi-save-config" className="flex items-center gap-1.5 text-xs uppercase tracking-wider bg-[#C19A6B] text-white px-3 py-1.5 hover:brightness-110 disabled:opacity-50"><Save className="w-3.5 h-3.5" /> {saving ? "…" : "Enregistrer"}</button>
        }>
          <div className="space-y-3">
            {Object.entries(config.ai_rates || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3">
                <span className="flex-1 text-sm text-zinc-300">{v.label}</span>
                <input type="number" step="0.001" min="0" value={v.unit_cost}
                  data-testid={`roi-rate-${k}`}
                  onChange={(e) => setConfig({ ...config, ai_rates: { ...config.ai_rates, [k]: { ...v, unit_cost: e.target.value } } })}
                  className="w-28 bg-[#0A0A0C] border border-white/15 px-3 py-1.5 text-sm text-white text-right" />
                <span className="text-xs text-zinc-500">€</span>
              </div>
            ))}
            <div className="flex items-center gap-3 pt-3 border-t border-white/10">
              <span className="flex-1 text-sm text-zinc-300">Frais Stripe (% du CA)</span>
              <input type="number" step="0.1" min="0" value={config.stripe_fee_pct}
                data-testid="roi-stripe-fee"
                onChange={(e) => setConfig({ ...config, stripe_fee_pct: e.target.value })}
                className="w-28 bg-[#0A0A0C] border border-white/15 px-3 py-1.5 text-sm text-white text-right" />
              <span className="text-xs text-zinc-500">%</span>
            </div>
          </div>
        </Panel>

        {/* Saisies manuelles */}
        <Panel title="Revenus / dépenses manuels" icon={Wallet}>
          <div className="flex flex-wrap gap-2 mb-4">
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="roi-manual-kind" className="bg-[#0A0A0C] border border-white/15 px-2 py-1.5 text-sm text-white">
              <option value="revenue">Revenu</option>
              <option value="expense">Dépense</option>
            </select>
            <input placeholder="Libellé" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="roi-manual-label" className="flex-1 min-w-[120px] bg-[#0A0A0C] border border-white/15 px-3 py-1.5 text-sm text-white" />
            <input type="number" placeholder="Montant €" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="roi-manual-amount" className="w-28 bg-[#0A0A0C] border border-white/15 px-3 py-1.5 text-sm text-white" />
            <button onClick={addManual} data-testid="roi-manual-add" className="flex items-center gap-1 text-xs uppercase tracking-wider bg-[#C19A6B] text-white px-3 py-1.5 hover:brightness-110"><Plus className="w-3.5 h-3.5" /> Ajouter</button>
          </div>
          {manual.length === 0 ? <p className="text-xs text-zinc-500">Aucune saisie manuelle.</p> : (
            <div className="divide-y divide-white/5" data-testid="roi-manual-list">
              {manual.map((e) => (
                <div key={e.id} className="flex items-center gap-3 py-2 text-sm">
                  <span className={`w-1.5 h-1.5 rounded-full ${e.kind === "revenue" ? "bg-emerald-400" : "bg-red-400"}`} />
                  <span className="flex-1 text-zinc-300 truncate">{e.label}</span>
                  <span className={e.kind === "revenue" ? "text-emerald-400" : "text-red-400"}>{e.kind === "revenue" ? "+" : "−"}{eur(e.amount)}</span>
                  <button onClick={() => delManual(e.id)} data-testid={`roi-manual-del-${e.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </AdminShell>
  );
}
